/**
 * ErrorBoundary — catch render-time errors y mostrar fallback en lugar
 * de tirar la página entera a negro.
 *
 * Sin esto, un bug en /industry (ej. data con shape inesperado) crashea
 * toda la SPA y el user ve pantalla en blanco — necesita hard-refresh +
 * puede perder state. Con boundary, sólo la ruta afectada muestra el
 * fallback, el resto del shell (nav, header) sigue funcionando.
 *
 * React requiere class component para `componentDidCatch` — no hay
 * alternativa con hooks todavía.
 *
 * NO captura:
 *   - Event handlers (onClick etc) — esos usan try/catch.
 *   - Async errors fuera del render path (Promises, setTimeout).
 *   - Server-side errors (no hay SSR acá).
 *
 * Recovery: botón "Reintentar" resetea el state interno y re-monta los
 * children. Si el error es determinístico, va a volver a tirar — útil
 * para que el user sepa que sigue roto, no que "se arregló solo".
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Nombre legible de la sección — aparece en el fallback. */
  label?: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log full info a la consola — el user puede copiarlo si quiere
    // reportar el bug. En prod esto también lo captura el browser.
    console.error(
      `[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`,
      error,
      info.componentStack,
    )
  }

  private handleRetry = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} label={this.props.label} onRetry={this.handleRetry} />
    }
    return this.props.children
  }
}

function ErrorFallback({
  error,
  label,
  onRetry,
}: {
  error: Error
  label?: string
  onRetry: () => void
}) {
  return (
    <div className="card border-eve-danger/30 max-w-2xl">
      <div className="flex items-start gap-3">
        <div className="text-eve-danger text-xl shrink-0 mt-0.5">⚠</div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-eve-text mb-1">
            Algo se rompió{label ? ` en ${label}` : ''}
          </h2>
          <p className="text-sm text-eve-muted mb-3">
            Esta vista no se pudo renderizar. El resto del dashboard sigue
            funcionando — podés navegar a otra sección desde el menú.
          </p>
          <details className="text-xs mb-3">
            <summary className="cursor-pointer text-eve-muted hover:text-eve-text">
              Ver detalle técnico
            </summary>
            <pre className="mt-2 p-2 bg-eve-bg border border-eve-border rounded text-[11px] overflow-auto max-h-48 whitespace-pre-wrap text-eve-danger">
              {error.name}: {error.message}
              {'\n\n'}
              {error.stack?.split('\n').slice(0, 8).join('\n')}
            </pre>
          </details>
          <div className="flex gap-2">
            <button
              onClick={onRetry}
              className="text-xs px-3 py-1.5 rounded-md border border-eve-border bg-eve-panel text-eve-text hover:border-eve-accent/40 transition-colors"
            >
              Reintentar
            </button>
            <a
              href="/"
              className="text-xs px-3 py-1.5 rounded-md border border-eve-border bg-eve-panel text-eve-muted hover:text-eve-text transition-colors"
            >
              Ir al Dashboard
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
